import {FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMachine} from "react-robot";
import {Button} from "@interstate/components/Button";
import {ChevronRightIcon} from "@interstate/components/Icons";
import {useGraphqlMutation} from "../../../amplify/hooks";
import {
  createOperationalEntityWithTenant as createOperationalEntityWithTenantMutation,
  createTenant as createTenantMutation
} from "../../../amplify/graphql";
import {useAlertContext} from "../../../alerting";
import {useFeedback} from "../../../feedback";
import {
  CreateOperationalEntityWithTenantData,
  CreateOperationalEntityWithTenantVariables,
  CreateTenantResponse,
  CreateTenantVariables
} from "../../backend";
import {CreateEntityPlan} from "./view-model/CreateEntityPlan";
import {CreateEntityResult} from "./state-machine";
import {stateMachine} from "./state-machine/stateMachine";
import {Nullable} from "../../../utils";
import {transformTestEntityToOperationalEntityInput} from "./stepper/utils/transforms";
import {v4 as uuidv4} from "uuid";
import {CreateNewEntityForm, CreateNewEntityFormData} from "./CreateNewEntityForm";
import {CreateNewEntityProvider} from "./stepper/context/CreateNewEntityProvider";
import {CreateNewEntityReviewStep} from "./stepper/steps/CreateNewEntityReviewStep";
import {OrganizationStepper} from "../create-organization/steps/common";
import {useNavigation} from "../../../navigation";
import type {OrganizationInformation} from "@common-core/coat-operational-hierarchy-appsync-model";
import "../create-organization/steps/common/BuilderStep.scss";
import "../create-organization/OrganizationBuilder.scss";

export const CreateEntityBuilder: FC = () => {
  const [formData, setFormData] = useState<CreateNewEntityFormData | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(null);
  const [validatedTenantName, setValidatedTenantName] = useState<string | null>(null);
  const {t} = useTranslation();
  const [, setAlert, clearAlert] = useAlertContext();
  const [, setFeedback] = useFeedback();
  const {navigateTo} = useNavigation();

  // Clear alert on unmount so it doesn't persist on other pages
  useEffect(() => {
    return () => clearAlert();
  }, [clearAlert]);

  const [createOperationalEntityWithTenant] = useGraphqlMutation<
    CreateOperationalEntityWithTenantData,
    CreateOperationalEntityWithTenantVariables
  >({
    amplifyMutation: createOperationalEntityWithTenantMutation
  });

  const [createTenant] = useGraphqlMutation<
    CreateTenantResponse,
    CreateTenantVariables
  >({
    amplifyMutation: createTenantMutation
  });

  /**
   * Creates a new tenant if the plan calls for one, or returns the existing tenant ID.
   *
   * @returns The resolved tenant ID, or undefined if tenant creation fails
   */
  const createTenantIfRequired = async (
    plan: CreateEntityPlan
  ): Promise<string | undefined> => {
    if (plan.tenantChoice.type === "existing") {
      return plan.tenantChoice.id;
    }

    // plan.tenantChoice.type === "new"
    try {
      const tenantResult = await createTenant({
        variables: {
          input: {
            name: plan.tenantChoice.name,
            synthetic: plan.tenantChoice.synthetic
          }
        }
      });

      if (!tenantResult.data?.createTenant?.tenant?.id) {
        return undefined;
      }

      return tenantResult.data.createTenant.tenant.id;
    } catch {
      return undefined;
    }
  };

  /**
   * Orchestrates tenant resolution and operational entity creation.
   *
   * On success, sends a "success" event to the state machine.
   * On any failure, sends a "failure" event with an appropriate message.
   */
  const createEntity = async (
    plan: CreateEntityPlan
  ): Promise<Nullable<CreateEntityResult>> => {
    const tenantId = await createTenantIfRequired(plan);

    if (!tenantId) {
      const message = t(
        "create-new-entity.errors.tenant-creation-failed",
        "Failed to resolve tenant. Please try again."
      );
      setAlert({message});
      send({
        type: "failure",
        plan,
        result: {
          entityId: "",
          tenantId: "",
          dbaName: plan.entity.dbaName,
          status: "failed",
          message
        }
      });
      return null;
    }

    const transactionId = uuidv4();

    // Build a minimal CreateNewEntityFormData-compatible object from the plan
    // so we can reuse the existing transform utility.
    const formData = {
      tenantChoice: plan.tenantChoice.type,
      tenantNewName:
        plan.tenantChoice.type === "new" ? plan.tenantChoice.name : "",
      tenantNewSynthetic:
        plan.tenantChoice.type === "new" ? plan.tenantChoice.synthetic : true,
      tenantExistingId:
        plan.tenantChoice.type === "existing" ? plan.tenantChoice.id : "",
      dbaName: plan.entity.dbaName,
      legalName: plan.entity.legalName,
      status: plan.entity.status,
      telecomNumbers: plan.entity.telecomNumbers,
      streetAddressLine1: plan.entity.streetAddressLine1,
      streetAddressLine2: plan.entity.streetAddressLine2,
      city: plan.entity.city,
      state: plan.entity.state,
      country: plan.entity.country,
      zip: plan.entity.zip
    };

    // Build the mutation input from the transform, then override tenantId with
    // the resolved ID (the transform may embed a placeholder for new tenants).
    const baseInput = transformTestEntityToOperationalEntityInput(
      formData,
      transactionId
    );
    const createOEInput = {...baseInput, tenantId};

    try {
      const oeResult = await createOperationalEntityWithTenant({
        variables: {createOEInput}
      });

      const entityId = oeResult.data?.createOEResult?.id;

      if (!entityId) {
        const message = t(
          "create-new-entity.errors.no-entity-id",
          "Entity was created but no ID was returned."
        );
        setAlert({message});
        send({
          type: "failure",
          plan,
          result: {
            entityId: "",
            tenantId,
            dbaName: plan.entity.dbaName,
            status: "failed",
            message
          }
        });
        return null;
      }

      const result: CreateEntityResult = {
        entityId,
        tenantId,
        dbaName: plan.entity.dbaName,
        status: oeResult.data?.createOEResult?.status ?? "success"
      };

      setCreatedEntityId(entityId);
      send({type: "success", plan, result});
      return result;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(
              "create-new-entity.errors.generic",
              "An unexpected error occurred."
            );

      setAlert({message});
      send({
        type: "failure",
        plan,
        result: {
          entityId: "",
          tenantId,
          dbaName: plan.entity.dbaName,
          status: "failed",
          message
        }
      });
      return null;
    }
  };

  const [{context, name: currentState}, send] = useMachine(stateMachine, {
    createEntity,
    translate: t,
    showFeedback: setFeedback,
    clearFeedback: () => setFeedback(undefined),
    review: (plan: CreateEntityPlan) => send({type: "review", plan}),
    create: (plan: CreateEntityPlan) => send({type: "create", plan}),
    retry: (plan: CreateEntityPlan) => send({type: "retry", plan}),
    stepBack: () => send({type: "step-back"})
  } as any);

  const buildPlanFromFormData = (data: CreateNewEntityFormData): CreateEntityPlan => ({
    tenantChoice: data.tenantChoice === "new"
      ? {type: "new", name: data.tenantNewName, synthetic: data.tenantNewSynthetic}
      : {type: "existing", id: data.tenantExistingId, name: validatedTenantName ?? data.tenantExistingId},
    entity: {
      dbaName: data.dbaName,
      legalName: data.legalName,
      status: data.status,
      telecomNumbers: data.telecomNumbers,
      streetAddressLine1: data.streetAddressLine1,
      streetAddressLine2: data.streetAddressLine2,
      city: data.city,
      state: data.state,
      country: data.country,
      zip: data.zip
    }
  });

  const handleFormReview = () => {
    if (formData && isFormValid) {
      const plan = buildPlanFromFormData(formData);
      send({type: "review", plan});
    }
  };

  const currentPlan = formData ? buildPlanFromFormData(formData) : null;

  const buildOrgInfo = (data: CreateNewEntityFormData): OrganizationInformation => ({
    dbaName: data.dbaName,
    legalName: data.legalName || data.dbaName,
    status: data.status,
    telecomNumbers: data.telecomNumbers ? [data.telecomNumbers] : [],
    address: data.streetAddressLine1
      ? {
          streetAddressLine1: data.streetAddressLine1,
          streetAddressLine2: data.streetAddressLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          zip: data.zip
        }
      : undefined
  });

  const handleCreate = () => {
    if (currentPlan) {
      send({type: "create", plan: currentPlan});
    }
  };

  const handleStepBack = () => {
    clearAlert();
    send({type: "step-back"});
  };

  const handleRetry = () => {
    clearAlert();
    if (currentPlan) {
      send({type: "retry", plan: currentPlan});
    }
  };

  // Derive tenant display name — use validated name for existing tenants when available
  const tenantDisplayName = formData
    ? formData.tenantChoice === "new"
      ? formData.tenantNewName
      : validatedTenantName ?? formData.tenantExistingId
    : "";

  const tenantChoice = formData?.tenantChoice ?? "new";

  // State-based rendering — all states handled directly by the Builder
  switch (currentState) {
    case "form":
      return (
        <section className="create-entity-builder">
          <CreateNewEntityProvider>
            <CreateNewEntityForm
              onFormDataChange={setFormData}
              onValidationChange={setIsFormValid}
              onTenantValidated={setValidatedTenantName}
            />
          </CreateNewEntityProvider>
          <div className="builder-controls">
            <Button
              data-testid="review-plan-button"
              buttonStyle="primary"
              disabled={!isFormValid}
              onClick={handleFormReview}>
              {t("create-new-entity.controls.review", "Review Plan")}
            </Button>
          </div>
        </section>
      );

    case "review-plan":
      return (
        <section
          data-testid="create-entity-review-plan"
          className="organization-builder organization-builder-step">
          <OrganizationStepper activeStep={0} />
          {formData && (
            <CreateNewEntityReviewStep
              transactionId="pending"
              organizationInformation={buildOrgInfo(formData)}
              oems={[]}
              tenantChoice={tenantChoice as "new" | "existing"}
              tenantName={tenantDisplayName}
              tenantId={
                tenantChoice === "existing"
                  ? formData.tenantExistingId
                  : undefined
              }
            />
          )}
          <div data-testid="stepper-controls" className="stepper-controls">
            <div
              data-testid="stepper-controls-left"
              className="stepper-controls-left"
            />
            <div
              data-testid="stepper-controls-right"
              className="stepper-controls-right">
              <Button
                data-testid="step-action-cancel-button"
                buttonStyle="tertiary"
                onClick={handleStepBack}>
                {t("create-new-entity.controls.cancel", "Cancel")}
              </Button>
              <Button
                data-testid="step-action-create-button"
                buttonStyle="primary"
                endIcon={<ChevronRightIcon />}
                onClick={handleCreate}>
                {t("create-new-entity.controls.finish", "Finish")}
              </Button>
            </div>
          </div>
        </section>
      );

    case "in-progress":
      return (
        <section
          data-testid="create-entity-in-progress"
          className="organization-builder organization-builder-step">
          <OrganizationStepper activeStep={1} reviewPlanCompleted />
          {formData && (
            <CreateNewEntityReviewStep
              transactionId="pending"
              organizationInformation={buildOrgInfo(formData)}
              oems={[]}
              tenantChoice={tenantChoice as "new" | "existing"}
              tenantName={tenantDisplayName}
              tenantId={tenantChoice === "existing" ? formData.tenantExistingId : undefined}
              status="in-progress"
            />
          )}
        </section>
      );

    case "success":
      return (
        <section
          data-testid="create-entity-success"
          className="organization-builder organization-builder-step">
          <OrganizationStepper
            activeStep={1}
            reviewPlanCompleted
            resultsCompleted
          />
          {formData && (
            <CreateNewEntityReviewStep
              transactionId="completed"
              organizationInformation={buildOrgInfo(formData)}
              oems={[]}
              tenantChoice={tenantChoice as "new" | "existing"}
              tenantName={tenantDisplayName}
              tenantId={tenantChoice === "existing" ? formData.tenantExistingId : undefined}
              status="created"
              entityId={createdEntityId ?? undefined}
            />
          )}
          <div data-testid="stepper-controls" className="stepper-controls">
            <div data-testid="stepper-controls-left" className="stepper-controls-left" />
            <div data-testid="stepper-controls-right" className="stepper-controls-right">
              <Button
                data-testid="back-to-dashboard-button"
                buttonStyle="tertiary"
                onClick={event => navigateTo(event, "/")}>
                {t("create-new-entity.controls.back-to-dashboard", "Back to Dashboard")}
              </Button>
            </div>
          </div>
        </section>
      );

    case "failure":
      return (
        <section
          data-testid="create-entity-failure"
          className="organization-builder organization-builder-step">
          <OrganizationStepper
            activeStep={1}
            reviewPlanCompleted
            resultsError
          />
          {formData && (
            <CreateNewEntityReviewStep
              transactionId="failed"
              organizationInformation={buildOrgInfo(formData)}
              oems={[]}
              tenantChoice={tenantChoice as "new" | "existing"}
              tenantName={tenantDisplayName}
              tenantId={tenantChoice === "existing" ? formData.tenantExistingId : undefined}
              status="failed"
            />
          )}
          <div data-testid="stepper-controls" className="stepper-controls">
            <div data-testid="stepper-controls-left" className="stepper-controls-left">
              <Button
                data-testid="step-back-button"
                buttonStyle="secondary"
                onClick={handleStepBack}>
                {t("create-new-entity.controls.back", "Back")}
              </Button>
            </div>
            <div data-testid="stepper-controls-right" className="stepper-controls-right">
              <Button
                data-testid="retry-button"
                buttonStyle="primary"
                onClick={handleRetry}>
                {t("create-new-entity.controls.retry", "Retry")}
              </Button>
            </div>
          </div>
        </section>
      );

    default:
      return null;
  }
};
